import { motion } from "framer-motion";
import { useEffect } from "react";

/**
 * Privacy Policy — powered by Termly (dynamic embed).
 * Termly injects the policy content into the div[data-id] via their embed-policy.min.js.
 */
const TERMLY_POLICY_ID = "7191c046-a603-4b80-bd49-81a01b24251e";
const TERMLY_SCRIPT_SRC = "https://app.termly.io/embed-policy.min.js";

const PrivacyPage = () => {
    useEffect(() => {
        // Load the Termly embed script once per page-view.
        if (document.querySelector(`script[src="${TERMLY_SCRIPT_SRC}"]`)) return undefined;
        const script = document.createElement("script");
        script.src = TERMLY_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
        return () => {
            // Leave the script; Termly re-uses it. No cleanup needed.
        };
    }, []);

    return (
        <main data-testid="privacy-page" className="relative">
            {/* Hero */}
            <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 border-b border-[var(--border)]">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="eyebrow-pill">Legal</span>
                        <h1 className="font-display text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            Privacy Policy.
                        </h1>
                        <p className="text-[var(--ink-soft)] mt-4 text-lg">
                            How Lexafide collects, uses, and protects information when you interact with our website and platform.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* DSAR request callout */}
            <section className="py-6">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <div
                        data-testid="dsar-callout"
                        className="rounded-2xl border border-[var(--border-strong)] bg-[var(--cream-2)] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5"
                    >
                        <div className="w-11 h-11 rounded-xl bg-[var(--blue-soft)] accent-blue flex items-center justify-center flex-shrink-0">
                            <i className="fa-solid fa-shield-halved text-[15px]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dim)] mb-1">
                                Your data rights
                            </div>
                            <div className="font-display text-[17px] md:text-[19px] font-semibold text-[var(--ink)] leading-tight">
                                Request access, correction, or deletion of your data
                            </div>
                            <p className="mt-1.5 text-[13.5px] text-[var(--ink-soft)] leading-relaxed">
                                Submit a Data Subject Access Request (DSAR) and we&apos;ll respond within the timeframe required by applicable law.
                            </p>
                        </div>
                        <a
                            href="https://app.termly.io/dsar/7191c046-a603-4b80-bd49-81a01b24251e"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="dsar-cta"
                            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--ink)] text-white font-medium text-[13px] hover:bg-[var(--blue)] transition-colors flex-shrink-0"
                        >
                            Submit a request
                            <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                        </a>
                    </div>
                </div>
            </section>

            {/* Termly embed */}
            <section className="py-12 md:py-20">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <div
                        data-testid="termly-embed"
                        name="termly-embed"
                        data-id={TERMLY_POLICY_ID}
                        className="termly-policy-container prose prose-neutral max-w-none"
                    />
                </div>
            </section>
        </main>
    );
};

export default PrivacyPage;
